/**
 * Reversible profile-packet key obfuscation for transit / Network inspection.
 * This is client-side obfuscation only — the secret ships with the app.
 *
 * TODO(crypto-v1): extend PacketScrambler with an optional CryptoPacketTransform
 * (Web Crypto AES-GCM over JSON bytes). Reserved encoding: 'crypto-v1'.
 */

export type PacketEncoding = 'key-scramble-v1' | 'crypto-v1';

export type ScrambledEnvelope = {
	schemaVersion: 1;
	encoding: PacketEncoding;
	data: unknown;
};

export interface PacketTransform {
	encoding: PacketEncoding;
	scramble(value: unknown): unknown;
	unscramble(value: unknown): unknown;
}

const ENVELOPE_SCHEMA_VERSION = 1;
const KEY_PREFIX = 'k.';

/** App-bundled obfuscation key — not a server secret. */
const KEY_SCRAMBLE_SECRET = 'shelly-profile-packet-v1';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const utf8Encode = (text: string): Uint8Array => {
	if (typeof TextEncoder !== 'undefined') {
		return new TextEncoder().encode(text);
	}
	const bytes = new Uint8Array(text.length);
	for (let i = 0; i < text.length; i++) {
		bytes[i] = text.charCodeAt(i) & 0xff;
	}
	return bytes;
};

const utf8Decode = (bytes: Uint8Array): string => {
	if (typeof TextDecoder !== 'undefined') {
		return new TextDecoder().decode(bytes);
	}
	let out = '';
	for (let i = 0; i < bytes.length; i++) {
		out += String.fromCharCode(bytes[i]);
	}
	return out;
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
	let binary = '';
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	const b64 =
		typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
	return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const base64UrlToBytes = (value: string): Uint8Array => {
	const padded = value.replace(/-/g, '+').replace(/_/g, '/');
	const padLen = (4 - (padded.length % 4)) % 4;
	const b64 = padded + '='.repeat(padLen);
	const binary =
		typeof atob !== 'undefined' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
};

const xorWithSecret = (bytes: Uint8Array, secret: string): Uint8Array => {
	const key = utf8Encode(secret);
	const out = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) {
		out[i] = bytes[i] ^ key[i % key.length];
	}
	return out;
};

const encodeObjectKey = (key: string, secret: string): string => {
	if (key.startsWith(KEY_PREFIX)) return key;
	return KEY_PREFIX + bytesToBase64Url(xorWithSecret(utf8Encode(key), secret));
};

const decodeObjectKey = (key: string, secret: string): string => {
	if (!key.startsWith(KEY_PREFIX)) return key;
	try {
		const payload = key.slice(KEY_PREFIX.length);
		return utf8Decode(xorWithSecret(base64UrlToBytes(payload), secret));
	} catch {
		return key;
	}
};

const transformKeysDeep = (value: unknown, mapKey: (key: string) => string): unknown => {
	if (Array.isArray(value)) {
		return value.map((item) => transformKeysDeep(item, mapKey));
	}
	if (!isPlainObject(value)) {
		return value;
	}
	const out: Record<string, unknown> = {};
	for (const [key, child] of Object.entries(value)) {
		out[mapKey(key)] = transformKeysDeep(child, mapKey);
	}
	return out;
};

/** Default transform: recursive object-key scramble; values unchanged. */
export const KeyScrambleV1: PacketTransform = {
	encoding: 'key-scramble-v1',
	scramble(value: unknown): unknown {
		return transformKeysDeep(value, (key) => encodeObjectKey(key, KEY_SCRAMBLE_SECRET));
	},
	unscramble(value: unknown): unknown {
		return transformKeysDeep(value, (key) => decodeObjectKey(key, KEY_SCRAMBLE_SECRET));
	},
};

/**
 * Reserved extension point for real encryption.
 * TODO(crypto-v1): implement scramble/unscramble with Web Crypto AES-GCM.
 */
export class CryptoPacketTransform implements PacketTransform {
	encoding: PacketEncoding = 'crypto-v1';

	scramble(_value: unknown): unknown {
		throw new Error('crypto-v1 is not implemented yet');
	}

	unscramble(_value: unknown): unknown {
		throw new Error('crypto-v1 is not implemented yet');
	}
}

export const isScrambledEnvelope = (value: unknown): value is ScrambledEnvelope => {
	if (!isPlainObject(value)) return false;
	if (value.schemaVersion !== ENVELOPE_SCHEMA_VERSION) return false;
	if (value.encoding !== 'key-scramble-v1' && value.encoding !== 'crypto-v1') return false;
	return 'data' in value;
};

export class PacketScrambler {
	constructor(private transform: PacketTransform = KeyScrambleV1) {}

	scramblePacket(plain: unknown): ScrambledEnvelope {
		return {
			schemaVersion: ENVELOPE_SCHEMA_VERSION,
			encoding: this.transform.encoding,
			data: this.transform.scramble(plain),
		};
	}

	/**
	 * Unwrap + unscramble an envelope, or pass through plain legacy payloads.
	 * Unknown encodings fail closed (return null).
	 */
	unscramblePacket(raw: unknown): unknown | null {
		if (!isScrambledEnvelope(raw)) {
			return raw;
		}

		if (raw.encoding === 'crypto-v1') {
			console.error('profile_packet_scrambler: crypto-v1 not implemented');
			return null;
		}

		if (raw.encoding !== this.transform.encoding && raw.encoding !== 'key-scramble-v1') {
			console.error(`profile_packet_scrambler: unknown encoding ${String(raw.encoding)}`);
			return null;
		}

		const transform = raw.encoding === this.transform.encoding ? this.transform : KeyScrambleV1;
		return transform.unscramble(raw.data);
	}
}

const defaultScrambler = new PacketScrambler(KeyScrambleV1);

export const scramblePacket = (plain: unknown): ScrambledEnvelope =>
	defaultScrambler.scramblePacket(plain);

export const unscramblePacket = (raw: unknown): unknown | null => defaultScrambler.unscramblePacket(raw);
